import { FC, useEffect, useState } from 'react';
import { useRouteError, isRouteErrorResponse, useLocation } from 'react-router';
import * as Sentry from '@sentry/react';
import { Flex, Paragraph } from '@wunder-mobility/ui-components';
import { Trans } from '@lingui/react/macro';
import ErrorFallback from '@/core/components/ErrorHandlers/ErrorFallback';
import UnauthLayout from '@/core/components/Layouts/Unauth';
import PageContent from '@/core/components/PageContent';
import styles from '@/core/assets/scss/components/ErrorHandlers/ErrorPage.module.scss';

const isProduction = import.meta.env.VITE_TARGET_ENV === 'production';

/**
 * Route-level error boundary for the data router (`createBrowserRouter`).
 *
 * React Router's data router catches errors thrown while rendering a route
 * element and renders the nearest `errorElement` instead of letting them
 * propagate. That means such errors never reach the Sentry `<ErrorBoundary>` in
 * `App.tsx` (it sits above `RouterProvider`) nor the global `window.onerror`
 * handler, so they were silently invisible to Sentry.
 *
 * This bridges those router-caught errors back into Sentry. In production it
 * shows a clean fallback; in staging/dev it surfaces the error details to make
 * debugging easier.
 */
const ErrorPage: FC = () => {
  const error = useRouteError();
  const location = useLocation();
  const [eventId, setEventId] = useState<string | null>(null);

  useEffect(() => {
    // Route error responses (4xx/5xx thrown by loaders) are not real exceptions,
    // so only report actual thrown errors to Sentry.
    if (error && !isRouteErrorResponse(error)) {
      // captureException returns the Sentry event id, which we surface below so
      // users/support can reference the exact event when tracking it in Sentry.
      setEventId(Sentry.captureException(error));
    }
  }, [error]);

  if (isProduction) {
    return (
      <UnauthLayout>
        <ErrorFallback
          message={
            <>
              <Trans id="errorFallback.defaultMessage">
                Try again or contact support for assistance.
              </Trans>
              {eventId && (
                <>
                  <br />
                  <Trans id="errorFallback.referenceId">Reference ID</Trans>: {eventId}
                </>
              )}
            </>
          }
        />
      </UnauthLayout>
    );
  }

  const renderErrorDetails = () => {
    if (isRouteErrorResponse(error)) {
      return (
        <>
          <Paragraph marginBottom={0} align="center" textColor="error">
            {error.status} {error.statusText}
          </Paragraph>
          <Paragraph align="center" textColor="error">
            Error occurred on: {location.pathname}
          </Paragraph>
          {error.data && (
            <pre className={styles['error-page__details']}>
              {typeof error.data === 'string' ? error.data : JSON.stringify(error.data, null, 2)}
            </pre>
          )}
        </>
      );
    }

    if (error instanceof Error) {
      return (
        <>
          <Paragraph marginBottom={0} align="center" textColor="error">
            {error.name}: {error.message}
          </Paragraph>
          <Paragraph align="center" textColor="error">
            Error occurred on: {location.pathname}
          </Paragraph>
          {error.stack && <pre className={styles['error-page__stack-trace']}>{error.stack}</pre>}
        </>
      );
    }

    return (
      <>
        <Paragraph marginBottom={0} align="center" textColor="error">
          {error?.toString()}
        </Paragraph>
        <Paragraph align="center" textColor="error">
          Error occurred on: {location.pathname}
        </Paragraph>
      </>
    );
  };

  return (
    <PageContent contentClassName={styles['error-page__content']}>
      <Flex direction="column" gap="xl">
        <ErrorFallback />

        <Flex direction="column" gap="sm" align="center">
          {eventId && (
            <Paragraph marginBottom={0} weight="bold" align="center" textColor="error">
              Reference ID: {eventId}
            </Paragraph>
          )}
          {renderErrorDetails()}
        </Flex>
      </Flex>
    </PageContent>
  );
};

export default ErrorPage;
